import React, { useEffect, useState } from 'react'
import { portal } from '../portalApi.js'
import { fmt, fmtDateTime } from '../api.js'
import NoteDrawer from '../components/NoteDrawer.jsx'

// 전표승인 — 데이터 화면에서 올라온 승인요청(submit)/수정요청(edit)을 처리
const KIND_LABEL = { bank: '계좌', tax: '세금계산서', card: '신용카드', securities: '증권' }
const TYPE_LABEL = { submit: '승인요청', edit: '수정요청' }
const STATUS_LABEL = { pending: '대기', approved: '승인', rejected: '반려' }

function Detail({ id, onDone }) {
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState(false)
  const [picked, setPicked] = useState(null) // null = 아직 안 건드림(= 대기 건 전부)

  useEffect(() => {
    portal.get(`/api/voucher/requests/${id}`).then(setData).catch((e) => setErr(e.message))
  }, [id])

  const decide = async (action) => {
    setBusy(true); setErr('')
    try {
      const r = await portal.post(`/api/voucher/requests/${id}/${action}`, {
        comment: comment || null,
        ticketIds: picked ? [...picked] : null,  // 안 고르면 남은 대기 건 전부
      })
      onDone(`#${id} ${r.count ?? ''}건 ${action === 'approve' ? '승인' : '반려'} 완료`)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  if (err) return <div className="error-box">{err}</div>
  if (!data) return <div className="muted">불러오는 중…</div>
  const isEdit = data.type === 'edit'

  // 스냅샷을 위하고 분개 양식(줄 단위)으로 편다. 한 거래가 여러 줄이 되고, 같은 거래는
  // 같은 전표번호를 공유한다. 금액은 차변/대변 열로 갈라 넣는다.
  const pendingIds = data.items
    .filter((it) => (it.status || 'pending') === 'pending')
    .map((it) => it.ticket_id)
  const chosen = picked ?? new Set(pendingIds)   // 아직 안 건드렸으면 대기 건 전부
  const toggle = (tid) => setPicked((p) => {
    const n = new Set(p ?? pendingIds)
    n.has(tid) ? n.delete(tid) : n.add(tid)
    return n
  })
  const allChosen = pendingIds.length > 0 && pendingIds.every((t) => chosen.has(t))
  const ITEM_STATUS = { approved: '승인', rejected: '반려' }

  const hasJournal = Object.keys(data.journal || {}).length > 0
  const sheet = []
  if (hasJournal) {
    let no = 0
    for (const it of data.items) {
      const lines = data.journal[String(it.ticket_id)] || []
      if (!lines.length) continue
      no += 1
      const [y, m, d] = String(it.date || '').split('-')
      let first = true
      for (const l of lines) {
        sheet.push({
          ticketId: it.ticket_id, 상태: it.status || 'pending', 첫줄: first,
          줄수: lines.length,
          년: y, 월: m ? String(Number(m)) : '', 일: d ? String(Number(d)) : '',
          번호: String(no).padStart(5, '0'),
          거래처코드: l['거래처코드'] || '', 거래처명: l['거래처명'] || '',
          구분: l['차대'] === '차' ? '차변' : '대변',
          계정코드: l['계정코드'] || '', 계정명: l['계정명'] || '',
          적요: l['적요'] || '',
          차변: l['차대'] === '차' ? l['금액'] : 0,
          대변: l['차대'] === '대' ? l['금액'] : 0,
        })
        first = false
      }
    }
  }

  return (
    <div className="detail-box">
      <div className="row" style={{ marginBottom: 8 }}>
        <b>#{data.id} · {KIND_LABEL[data.kind]} · {TYPE_LABEL[data.type]}</b>
        <span className="muted">요청 {data.requester?.name} → 담당 {data.approver?.name}</span>
        {data.memo && <span className="muted">메모: {data.memo}</span>}
        {data.status !== 'pending' && (
          <span className={data.status === 'approved' ? 'ok' : 'warn'}>
            {STATUS_LABEL[data.status]} · {data.decidedBy?.name} {data.decisionComment ? `· ${data.decisionComment}` : ''}
          </span>
        )}
      </div>
      {hasJournal ? (
        /* 위하고 분개 양식 그대로 - 결재자가 보는 건 계정과목 한 칸이 아니라 이 차·대다.
           전표번호는 요청 안에서 거래 단위로 매긴 일련번호(위하고가 매기는 번호와는 별개). */
        <table className="journal-sheet">
          <thead>
            <tr>
              <th style={{ width: 30 }}>
                <input type="checkbox" checked={allChosen} disabled={!pendingIds.length}
                  onChange={(e) => setPicked(e.target.checked ? new Set(pendingIds) : new Set())} />
              </th>
              <th>년</th><th>월</th><th>일</th><th>번호</th>
              <th>Code</th><th className="l">거래처</th>
              <th>구분</th>
              <th>Code</th><th className="l">계정과목</th>
              <th className="l">적요</th>
              <th>차변(출금)</th><th>대변(입금)</th>
            </tr>
          </thead>
          <tbody>
            {sheet.map((row, i) => (
              <tr key={i} className={row.상태 !== 'pending' ? 'decided' : undefined}>
                {row.첫줄 ? (
                  <td className="c" rowSpan={row.줄수}>
                    {row.상태 === 'pending'
                      ? <input type="checkbox" checked={chosen.has(row.ticketId)}
                          onChange={() => toggle(row.ticketId)} />
                      : <span className="muted" style={{ fontSize: 11 }}>{ITEM_STATUS[row.상태]}</span>}
                  </td>
                ) : null}
                <td className="c">{row.년}</td><td className="c">{row.월}</td><td className="c">{row.일}</td>
                <td className="c">{row.번호}</td>
                <td className="c">{row.거래처코드}</td><td className="l">{row.거래처명}</td>
                <td className="c">{row.구분}</td>
                <td className="c">{row.계정코드}</td><td className="l">{row.계정명}</td>
                <td className="l">{row.적요}</td>
                <td>{row.차변 ? fmt(row.차변) : ''}</td>
                <td>{row.대변 ? fmt(row.대변) : ''}</td>
              </tr>
            ))}
            <tr className="total">
              <td colSpan={11} className="l">
                합계 <span className="muted">· 선택 {chosen.size}건 / 대기 {pendingIds.length}건</span>
              </td>
              <td>{fmt(sheet.reduce((a, r) => a + (r.차변 || 0), 0))}</td>
              <td>{fmt(sheet.reduce((a, r) => a + (r.대변 || 0), 0))}</td>
            </tr>
          </tbody>
        </table>
      ) : (
        <table>
          <thead>
            <tr>
              <th>일자</th><th className="l">거래상대</th><th className="l">우리계좌</th>
              <th>입금</th><th>출금</th><th>금액</th>
              <th className="l">{isEdit ? '적요 (기존→변경)' : '적요'}</th>
              <th className="l">{isEdit ? '계정과목 (기존→변경)' : '계정과목'}</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((it) => (
              <tr key={it.id}>
                <td className="c">{it.date}</td>
                <td className="l">{it.counterparty}</td>
                <td className="l">{it.our_account}</td>
                <td>{it.deposit ? fmt(it.deposit) : ''}</td>
                <td>{it.withdraw ? fmt(it.withdraw) : ''}</td>
                <td>{it.amount ? fmt(it.amount) : ''}</td>
                <td className="l">
                  {isEdit && it.new_description !== null
                    ? <>{it.prev_description || '-'} → <b>{it.new_description}</b></>
                    : (it.prev_description || '')}
                </td>
                <td className="l">
                  {isEdit && it.new_category_name !== null
                    ? <>{it.prev_category_name || '-'} → <b>{it.new_category_name}</b></>
                    : (it.prev_category_name || '')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {data.status === 'pending' && (
        <div className="row" style={{ marginTop: 10 }}>
          <input placeholder="코멘트(선택)" style={{ width: 240 }}
            value={comment} onChange={(e) => setComment(e.target.value)} />
          {hasJournal && <span className="muted">선택 {chosen.size}건</span>}
          <button className="btn" disabled={busy || (hasJournal && !chosen.size)}
            onClick={() => decide('approve')}>승인</button>
          <button className="btn danger" disabled={busy || (hasJournal && !chosen.size)}
            onClick={() => decide('reject')}>반려</button>
        </div>
      )}
    </div>
  )
}

export default function Approval({ currentUserId, autoOpenNoteId, onAutoOpened }) {
  const [box, setBox] = useState('inbox')
  const [status, setStatus] = useState('pending')
  const [list, setList] = useState(null)
  const [openId, setOpenId] = useState(null)
  const [err, setErr] = useState('')
  const [toast, setToast] = useState('')

  const load = () => {
    setErr('')
    const qs = new URLSearchParams({ box })
    if (status) qs.set('status', status)
    portal.get(`/api/voucher/requests?${qs}`)
      .then(setList)
      .catch((e) => setErr(e.status === 403 ? '전표승인 화면 권한이 없습니다.' : e.message))
  }
  useEffect(() => { load() }, [box, status])

  const done = (msg) => {
    setToast(msg); setTimeout(() => setToast(''), 3000)
    setOpenId(null); load()
  }

  return (
    <>
      <div style={{ position: 'relative' }}>
        <h2>전표승인</h2>
        <NoteDrawer target="approval" screenKey="approval"
          context={null} contextLabel={null} onNavigate={() => {}}
          autoOpenNoteId={autoOpenNoteId} onAutoOpened={onAutoOpened}
          currentUserId={currentUserId} />
      </div>
      <div className="row" style={{ marginBottom: 12 }}>
        <button className={`btn ${box === 'inbox' ? '' : 'secondary'}`} onClick={() => { setBox('inbox'); setOpenId(null) }}>받은 요청</button>
        <button className={`btn ${box === 'outbox' ? '' : 'secondary'}`} onClick={() => { setBox('outbox'); setOpenId(null) }}>보낸 요청</button>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setOpenId(null) }}>
          <option value="pending">대기</option>
          <option value="approved">승인</option>
          <option value="rejected">반려</option>
          <option value="">전체</option>
        </select>
      </div>
      {err && <div className="error-box">{err}</div>}
      {!err && !list && <div className="muted">불러오는 중…</div>}
      {!err && list && (
        <div className="panel">
          <table>
            <thead>
              <tr><th>번호</th><th>구분</th><th>유형</th><th>건수</th><th className="l">요청자</th><th className="l">담당자</th><th className="l">메모</th><th>요청일시</th><th>상태</th><th /></tr>
            </thead>
            <tbody>
              {list.length === 0 && <tr><td colSpan={10} className="c muted">요청이 없습니다</td></tr>}
              {list.map((r) => (
                <React.Fragment key={r.id}>
                  <tr>
                    <td className="c">#{r.id}</td>
                    <td className="c">{KIND_LABEL[r.kind]}</td>
                    <td className="c">{TYPE_LABEL[r.type]}</td>
                    <td>{fmt(r.item_count)}</td>
                    <td className="l">{r.requester?.name}</td>
                    <td className="l">{r.approver?.name}</td>
                    <td className="l">{r.memo}</td>
                    <td className="c">{fmtDateTime(r.created_at)}</td>
                    <td className="c">
                      <span className={r.status === 'approved' ? 'ok' : r.status === 'rejected' ? 'warn' : ''}>
                        {STATUS_LABEL[r.status]}
                      </span>
                    </td>
                    <td className="c">
                      <button className="btn secondary" onClick={() => setOpenId(openId === r.id ? null : r.id)}>
                        {openId === r.id ? '닫기' : '상세'}
                      </button>
                    </td>
                  </tr>
                  {openId === r.id && (
                    <tr><td colSpan={10} style={{ background: '#F8FAFC' }}><Detail id={r.id} onDone={done} /></td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {toast && <div className="toast">{toast}</div>}
    </>
  )
}
